<template>
  <!-- AI AutoPilot's chat as the app draws it (Workspace369 origin/main, Oct 22 2026), at the app's own scale.
       Wide containers get the desktop shell: SidebarV2 with AI AutoPilot › Chat, then AskAiScreen embedded
       (ask_ai_screen.dart): the chat header with the title, New chat and More; the chats rail (268pt) once the
       chat is 1000pt wide (ask_ai_chats_rail.dart); the 720pt conversation column; the docked composer
       (ai_input_field.dart). Below 600px it is the phone chat. A turn: the user's bubble, the step rows
       (AskAiPhoneStepRow: done, running, paused for approval), the reply under its "AI AutoPilot" label, and
       PendingActionCard, which stops before an email reaches a client. The header's balance and the composer's
       usage line are left out. Strings are app_en.arb's and function_label_map.dart's; Northstar Studio,
       Juniper Bakery, the invoice and the email are sample data. The turn walks ask (finding the client) →
       draft (the invoice found, the reply being prepared) → approve (the reply and the approval card); the
       server renders `approve`. The thread is top-anchored and only scrolls by transform, so nothing on the
       page changes size. Radius exceptions, all the app's own: the user bubble 18 (tail 6), the composer 14,
       the approval card 14, its "Needs approval" pill and the header's "Working..." (AppStatusPill's fully round ends), the round step nodes and send button. -->
  <div ref="root" class="acx" :class="`acx--${state}`" aria-hidden="true">
    <svg class="acx-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`acx-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="acx-desk">
      <aside class="acx-side">
        <div class="acx-side-top"><svg class="ic"><use href="#acx-view_sidebar" /></svg></div>
        <div class="acx-ws"><span class="acx-logo">N</span><span class="acx-ell">Northstar Studio</span><svg class="ic sm"><use href="#acx-unfold_more" /></svg></div>
        <nav class="acx-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="acx-ni top">
              <svg class="ic"><use :href="`#acx-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids" class="ic sm acx-chev"><use :href="`#acx-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c.k" class="acx-ni child" :class="{ sel: c.sel }"><svg class="ic"><use :href="`#acx-${c.i}`" /></svg><span>{{ c.k }}</span></div>
            </template>
          </template>
        </nav>
        <div class="acx-side-foot">
          <div v-for="f in foot" :key="f.k" class="acx-ni"><svg class="ic"><use :href="`#acx-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="acx-main">
        <header class="acx-top">
          <span class="acx-ttl"><svg class="ic sm"><use href="#acx-chatBubbleOutline" /></svg>Chat</span>
          <span class="acx-hsearch"><svg class="ic sm"><use href="#acx-searchIcon" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="acx-icbtn acx-bell"><svg class="ic"><use href="#acx-notifications" /></svg><b>3</b></span>
          <span class="acx-icbtn"><svg class="ic"><use href="#acx-settings" /></svg></span>
          <span class="acx-icbtn"><svg class="ic"><use href="#acx-chat_bubble_outline" /></svg></span>
        </header>
        <div class="acx-sheet">
          <aside class="acx-rail">
            <div class="acx-rail-head"><b>Chats</b><span class="acx-ghost"><svg class="ic"><use href="#acx-editSquare" /></svg></span></div>
            <div class="acx-rail-search"><svg class="ic sm"><use href="#acx-searchIcon" /></svg>Search</div>
            <div class="acx-rail-day">Today</div>
            <div class="acx-rail-chat on">{{ title }}</div>
            <div class="acx-rail-chat">Plan my day</div>
            <div class="acx-rail-day">Yesterday</div>
            <div class="acx-rail-chat">Proposal for Harbor &amp; Co.</div>
            <div class="acx-rail-chat">Who hasn't paid this month?</div>
            <div class="acx-rail-foot">View all</div>
          </aside>
          <section class="acx-chat">
            <header class="acx-head">
              <b class="acx-ell">{{ title }}</b>
              <span v-if="state !== 'approve'" class="acx-working">Working...</span>
              <i class="acx-grow" />
              <span class="acx-newchat"><svg class="ic"><use href="#acx-editSquare" /></svg>New chat</span>
              <span class="acx-ghost"><svg class="ic"><use href="#acx-moreHoriz" /></svg></span>
            </header>
            <div class="acx-view"><Thread /></div>
            <Composer />
          </section>
        </div>
      </div>
    </div>

    <!-- Phone -->
    <div class="acx-phone">
      <header class="acp-bar">
        <svg class="ic acp-back"><use href="#acx-chevronLeft" /></svg>
        <b class="acx-ell">{{ title }}</b>
        <svg class="ic"><use href="#acx-moreHoriz" /></svg>
        <svg class="ic acp-new"><use href="#acx-editSquare" /></svg>
      </header>
      <div class="acx-view"><Thread /></div>
      <Composer />
    </div>
  </div>
</template>

<script setup>
import { h, nextTick, onMounted, ref, watch } from "vue";
import { appIcons } from "~/data/app-icons.js";
import { aiChatIcons } from "~/data/app-icons-aichat.js";

const props = defineProps({
  // ask | draft | approve
  state: { type: String, default: "approve" },
});

const shared = ["account_balance_wallet", "chevron_right", "dashboard", "expand_more", "chat_bubble_outline", "forum", "handyman", "logout", "notifications", "people_alt", "settings", "support_agent", "unfold_more", "view_sidebar", "work_outline"];
const icons = { ...Object.fromEntries(shared.filter((k) => appIcons[k]).map((k) => [k, appIcons[k]])), ...aiChatIcons };

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "autoAwesome", open: true, kids: [{ k: "Today", i: "wbSunny" }, { k: "Chat", i: "chatBubbleOutline", sel: true }] },
  { k: "Clients", i: "people_alt", kids: [] },
  { k: "Work", i: "work_outline", kids: [] },
  { k: "Finance", i: "account_balance_wallet", kids: [] },
  { k: "Communications", i: "forum", kids: [] },
  { k: "Tools", i: "handyman", kids: [] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const title = "Reminder for Juniper Bakery";

// The approve state scrolls each thread to its end: measured, then moved by transform (never a layout change).
const root = ref(null);
const place = () => {
  for (const view of root.value?.querySelectorAll(".acx-view") || []) {
    const thread = view.firstElementChild;
    let end = 0;
    if (props.state === "approve") {
      // Scroll to the end, then on to the next whole row so no row is cut at the top edge.
      const need = thread.offsetHeight - view.clientHeight;
      const pad = parseFloat(getComputedStyle(thread).paddingTop) || 0;
      const rows = [...thread.querySelectorAll(".acx-user, .acx-label, .acx-step, .acx-reply")].map((r) => r.offsetTop - pad);
      const snap = rows.find((t) => t >= need);
      end = need > 0 ? -(snap ?? need) : 0;
    }
    thread.style.setProperty("--shift", `${end}px`);
  }
};
onMounted(() => {
  place();
  document.fonts?.ready.then(place);
});
watch(() => props.state, () => nextTick(place));

const ic = (name, cls = "ic") => h("svg", { class: cls }, [h("use", { href: `#acx-${name}` })]);

// AskAiPhoneStepRow: a round node (check when done, spinner while running, pause while it waits for you).
const Step = (node, label, detail) =>
  h("div", { class: ["acx-step", node] }, [
    h("span", { class: "acx-node" }, node === "done" ? [ic("checkIcon")] : node === "wait" ? [ic("pauseIcon")] : [h("i")]),
    h("span", [label, detail ? h("b", ` · ${detail}`) : null]),
  ]);

const steps = (s) =>
  s === "ask"
    ? [Step("run", "Finding clients", "Juniper Bakery")]
    : s === "draft"
      ? [Step("done", "Finding clients", "Juniper Bakery"), Step("done", "Finding invoices", "Invoice 1031"), Step("run", "Preparing your response")]
      : [Step("done", "Finding clients", "Juniper Bakery"), Step("done", "Finding invoices", "Invoice 1031"), Step("wait", "Sending invoice email")];

const Thread = () =>
  h("div", { class: "acx-thread" }, [
    h("div", { class: "acx-user" }, [h("span", "Remind Juniper Bakery about invoice 1031.")]),
    h("div", { class: "acx-turn" }, [
      h("div", { class: "acx-label" }, [ic("autoAwesome"), "AI AutoPilot"]),
      h("div", { class: "acx-steps" }, steps(props.state)),
      h("div", { class: "acx-reply" }, [
        h("p", "I drafted a reminder to Lena and stopped before sending it."),
      ]),
      // PendingActionCard (pending_action_card.dart)
      h("div", { class: "acx-card" }, [
        h("div", { class: "acx-card-head" }, [
          h("span", { class: "acx-tile" }, [ic("sendIcon")]),
          h("span", { class: "acx-card-t" }, [h("b", "Send the reminder for invoice 1031"), h("small", "Juniper Bakery · Invoice 1031")]),
          h("em", { class: "acx-pill" }, "Needs approval"),
        ]),
        h("div", { class: "acx-card-body" }, [
          h("div", { class: "acx-kv" }, [h("span", "To"), h("b", "lena@juniperbakery.example"), h("span", "Subject"), h("b", "Invoice 1031 is past due")]),
          h("div", { class: "acx-inset" }, "Hi Lena, a quick reminder that invoice 1031 for $1,300.00 was due on Oct 19. You can pay it online from the link in this email. Thank you!"),
        ]),
        h("div", { class: "acx-card-foot" }, [
          h("span", { class: "acx-cancel" }, "Cancel"),
          h("span", { class: "acx-send" }, [ic("checkIcon"), "Send Email"]),
        ]),
      ]),
    ]),
  ]);

const Composer = () =>
  h("div", { class: "acx-compose" }, [
    h("div", { class: "acx-box" }, [
      h("div", { class: "acx-ph" }, "Ask anything..."),
      h("div", { class: "acx-tools" }, [
        h("span", { class: "acx-plus" }, [ic("addIcon")]),
        h("span", { class: "acx-at" }, [ic("alternateEmail")]),
        h("i", { class: "acx-div" }),
        h("span", { class: "acx-chip" }, [ic("businessIcon"), "Workspace only"]),
        h("span", { class: "acx-chip" }, [ic("lockIcon"), "Ask first"]),
        h("span", { class: "acx-chip" }, [ic("autoAwesome"), h("em", "Effort"), "Auto", ic("keyboardArrowDown")]),
        h("i", { class: "acx-grow" }),
        h("span", { class: "acx-mic" }, [ic("micNone")]),
        h("span", { class: "acx-go" }, [ic("arrowUpward")]),
      ]),
    ]),
  ]);
</script>

<style>
/* Tokens: shadcn Neutral (FlexScheme index 58) as in InvoicesAppWindow, with AutoPilot's accent #4F46E5 and the
   approval card's orange tone (AppTone). */
.acx {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa; --accent: #4f46e5;
  --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --hair: rgb(10 10 10 / 0.12);
  --ink2: #4f4f4f; --muted: #6c6c6c; --faint: #8f8f8f; --selected: rgb(10 10 10 / 0.06);
  --bubble: rgb(10 10 10 / 0.06); --node: rgb(10 10 10 / 0.055); --inset: #f7f9fb; --rail: #fafafa;
  --tone-t: #ad4e00; --tone-f: #fcedd6;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.3;
  text-align: left;
}
.dark .acx {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717; --accent: #a5b4fc;
  --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --hair: rgb(250 250 250 / 0.12);
  --ink2: #c2c2c2; --muted: #a3a3a3; --faint: #7a7a7a; --selected: rgb(250 250 250 / 0.1);
  --bubble: rgb(250 250 250 / 0.1); --node: rgb(250 250 250 / 0.08); --inset: #212325; --rail: #1d1d1d;
  --tone-t: #f2b26b; --tone-f: rgb(230 140 50 / 0.18);
}
/* Size reserved before the markup streams in (the G1 pattern): 600 / 540 / the phone's 390:700. */
.acx { min-height: 600px; }
@media (max-width: 1128px) { .acx { min-height: 540px; } }
@media (max-width: 648px) { .acx { max-width: 390px; min-height: 0; margin-inline: auto; aspect-ratio: 390 / 700; } }
.acx-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.acx .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.acx .ic.sm { width: 16px; height: 16px; }
.acx-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.acx-grow { flex: 1; }

/* ---------- desktop shell ---------- */
.acx-desk {
  display: grid; grid-template-columns: 252px minmax(0, 1fr); height: 600px; overflow: hidden;
  border: 1px solid var(--card-edge); border-radius: 10px; background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
}
.dark .acx-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.acx-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.acx-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.acx-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.acx-ws .ic { margin-left: auto; color: var(--faint); }
.acx-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .acx-logo { background: #fafafa; color: #171717; }
.acx-nav { flex: 1; min-height: 0; overflow: hidden; }
.acx-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.acx-ni.top { color: var(--ink); font-weight: 600; }
.acx-ni.child { margin-left: 12px; }
.acx-ni.child .ic { color: var(--faint); }
.acx-ni.sel { background: var(--selected); color: var(--ink); font-weight: 500; }
.acx-ni .acx-chev { margin-left: auto; color: var(--faint); }
.acx-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.acx-side-foot .acx-ni { height: 30px; }
.acx-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; min-height: 0; }
.acx-top { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.acx-ttl { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.acx-ttl .ic { color: var(--ink2); }
.acx-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--faint); }
.acx-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.acx-icbtn { position: relative; display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.acx-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.acx-sheet { display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); }

/* The chats rail, once the chat itself is 1000 wide. */
.acx-rail { display: none; flex-direction: column; border-right: 1px solid var(--hair); background: var(--rail); padding: 12px 8px 0 16px; }
.acx-rail-head { display: flex; align-items: center; justify-content: space-between; height: 36px; font-size: 14px; }
.acx-rail-search { display: flex; align-items: center; gap: 8px; height: 32px; margin: 6px 8px 10px 0; border: 1px solid var(--hair); border-radius: 8px; padding: 0 10px; color: var(--faint); }
.acx-rail-day { margin: 10px 0 4px; color: var(--faint); font-size: 11px; font-weight: 600; }
.acx-rail-chat { overflow: hidden; margin-right: 8px; border-radius: 8px; padding: 7px 10px; color: var(--ink2); text-overflow: ellipsis; white-space: nowrap; }
.acx-rail-chat.on { border: 1px solid var(--hair); background: var(--page); color: var(--ink); font-weight: 500; }
.acx-rail-foot { margin-top: auto; border-top: 1px solid var(--hair); padding: 12px 0 14px; color: var(--muted); font-weight: 500; }
@container (min-width: 1260px) {
  .acx-sheet { grid-template-columns: 268px minmax(0, 1fr); }
  .acx-rail { display: flex; }
}

.acx-chat { position: relative; display: grid; grid-template-rows: 52px minmax(0, 1fr) auto; min-width: 0; min-height: 0; }
.acx-head { display: flex; align-items: center; gap: 10px; border-bottom: 1px solid var(--hair); padding: 0 12px 0 24px; }
.acx-head b { font-size: 15px; font-weight: 600; letter-spacing: -0.01em; }
.acx-working { flex: none; border-radius: 99px; background: var(--node); padding: 2px 8px; color: var(--muted); font-size: 12px; font-weight: 500; }
.acx-newchat { display: inline-flex; align-items: center; gap: 6px; height: 32px; border-radius: 8px; padding: 0 10px; font-weight: 500; }
.acx-ghost { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 8px; color: var(--ink2); }

/* The conversation: a 720 column; the approve state scrolls it to its end by transform. */
.acx-view { position: relative; min-height: 0; overflow: hidden; }
/* Scrolled, the top edge fades as the app's list does under the header. */
.acx--approve .acx-view { -webkit-mask-image: linear-gradient(transparent, #000 16px); mask-image: linear-gradient(transparent, #000 16px); }
.acx-thread { max-width: 720px; margin: 0 auto; padding: 24px 24px 20px; transition: transform 0.8s cubic-bezier(0.45, 0, 0.2, 1); }
.acx-thread { transform: translateY(var(--shift, 0px)); }
.acx-user { display: flex; justify-content: flex-end; }
.acx-user span { max-width: 75%; border-radius: 18px 18px 6px 18px; background: var(--bubble); padding: 10px 15px; font-size: 15px; line-height: 1.45; }
.acx-turn { margin-top: 24px; }
.acx-label { display: flex; align-items: center; gap: 6px; color: var(--accent); font-weight: 600; }
.acx-label .ic { width: 15px; height: 15px; }
.acx-steps { display: grid; gap: 10px; margin-top: 10px; }
.acx-step { display: flex; align-items: center; gap: 10px; color: var(--muted); font-size: 14.5px; line-height: 1.45; animation: acx-in 0.35s ease-out; }
.acx-step b { color: var(--ink); font-weight: 400; }
.acx-node { display: grid; flex: none; place-items: center; width: 20px; height: 20px; border-radius: 50%; background: var(--node); color: var(--muted); }
.acx-node .ic { width: 12px; height: 12px; }
.acx-step.run { color: var(--ink); font-weight: 500; }
.acx-step.run .acx-node { background: none; }
.acx-step.run .acx-node i { width: 14px; height: 14px; border: 2px solid rgb(79 70 229 / 0.2); border-top-color: var(--accent); border-radius: 50%; animation: acx-spin 0.9s linear infinite; }
.acx-step.wait { color: var(--ink); font-weight: 500; }
.acx-step.wait .acx-node { background: var(--tone-f); color: var(--tone-t); }
.acx-reply, .acx-card { visibility: hidden; opacity: 0; }
.acx--approve .acx-reply, .acx--approve .acx-card { visibility: visible; opacity: 1; transition: opacity 0.5s ease 0.35s; }
.acx-reply p { margin: 14px 0 0; font-size: 15px; line-height: 1.55; }
.acx-card { max-width: 560px; margin-top: 12px; overflow: hidden; border: 1px solid var(--hair); border-radius: 14px; background: var(--page); box-shadow: 0 1px 2px rgb(0 0 0 / 0.04), 0 6px 20px rgb(0 0 0 / 0.06); }
.acx-card-head { display: flex; align-items: center; gap: 12px; padding: 14px 16px; }
.acx-tile { display: grid; flex: none; place-items: center; width: 36px; height: 36px; border-radius: 10px; background: var(--tone-f); color: var(--tone-t); }
.acx-card-t { display: grid; flex: 1; gap: 2px; min-width: 0; }
.acx-card-t b { font-size: 14.5px; font-weight: 600; }
.acx-card-t small { color: var(--muted); font-size: 12.5px; }
.acx-pill { flex: none; border-radius: 99px; background: var(--tone-f); padding: 3px 9px; color: var(--tone-t); font-size: 12px; font-style: normal; font-weight: 500; }
.acx-card-body { border-top: 1px solid var(--hair); padding: 12px 16px 14px; }
.acx-kv { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 8px 12px; font-size: 14px; }
.acx-kv span { color: var(--muted); }
.acx-kv b { overflow: hidden; font-weight: 400; text-overflow: ellipsis; white-space: nowrap; }
.acx-inset { margin-top: 12px; border: 1px solid var(--hair); border-radius: 10px; background: var(--inset); padding: 10px 12px; color: var(--muted); font-size: 13.5px; line-height: 1.5; }
.acx-card-foot { display: flex; align-items: center; justify-content: flex-end; gap: 16px; border-top: 1px solid var(--hair); background: var(--inset); padding: 12px 16px; }
.acx-cancel { color: var(--muted); font-weight: 500; }
.acx-send { display: inline-flex; align-items: center; gap: 6px; height: 36px; border-radius: 8px; background: var(--primary); padding: 0 14px; color: var(--on-primary); font-weight: 500; }
.acx-send .ic { width: 16px; height: 16px; }

/* The docked composer. */
.acx-compose { padding: 0 24px 16px; }
.acx-box { max-width: 800px; margin: 0 auto; border: 1px solid var(--hair); border-radius: 14px; background: var(--page); box-shadow: 0 2px 10px rgb(0 0 0 / 0.05); padding: 12px 10px 10px 14px; }
.acx-ph { color: var(--faint); font-size: 15px; }
.acx-tools { display: flex; align-items: center; gap: 6px; margin-top: 12px; }
.acx-plus { display: grid; place-items: center; width: 32px; height: 32px; border: 1px solid var(--hair); border-radius: 50%; color: var(--ink2); }
.acx-at, .acx-mic { display: grid; place-items: center; width: 32px; height: 32px; color: var(--ink2); }
.acx-div { width: 1px; height: 18px; margin: 0 4px; background: var(--hair); }
.acx-chip { display: inline-flex; align-items: center; gap: 5px; height: 30px; border-radius: 8px; padding: 0 9px; font-weight: 500; white-space: nowrap; }
.acx-chip .ic { width: 16px; height: 16px; color: var(--ink2); }
.acx-chip em { color: var(--muted); font-style: normal; }
.acx-go { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 50%; background: var(--node); color: var(--faint); }
.acx-go .ic { width: 20px; height: 20px; }
@keyframes acx-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes acx-spin { to { transform: rotate(360deg); } }

/* Tablet: the sidebar collapsed, the toolbar's later chips dropped as the composer narrows. */
@container (max-width: 1080px) {
  .acx-desk { grid-template-columns: minmax(0, 1fr); height: 540px; }
  .acx-side { display: none; }
  .acx-top { padding-left: 16px; }
}
@container (max-width: 760px) {
  .acx-chip:nth-of-type(n + 4) { display: none; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.acx-phone { display: none; }
@container (max-width: 600px) {
  .acx-desk { display: none; }
  .acx-phone {
    --u: calc(100cqi / 390); --t: calc(var(--u) * 1.25);
    position: relative; display: flex; flex-direction: column; height: calc(var(--u) * 700); overflow: hidden;
    border: 1px solid var(--card-edge); border-radius: 10px; background: var(--page); font-size: calc(var(--t) * 13);
  }
  .dark .acx-phone { border-color: #3a3a3a; }
  .acx-phone .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .acp-bar { display: flex; flex: none; align-items: center; gap: calc(var(--u) * 16); height: calc(var(--u) * 56); border-bottom: 1px solid var(--hair); padding: 0 calc(var(--u) * 16); }
  .acx-phone .acp-back { width: calc(var(--u) * 26); height: calc(var(--u) * 26); }
  .acx-phone .acp-new { width: calc(var(--u) * 21); height: calc(var(--u) * 21); }
  .acp-bar b { flex: 1; min-width: 0; font-size: calc(var(--t) * 16); font-weight: 600; }
  .acx-phone .acx-view { flex: 1; }
  .acx-phone .acx-thread { padding: calc(var(--u) * 16) calc(var(--u) * 16) calc(var(--u) * 16); }
  .acx-phone .acx-user span { max-width: 80%; border-radius: calc(var(--u) * 20) calc(var(--u) * 20) calc(var(--u) * 6) calc(var(--u) * 20); padding: calc(var(--u) * 10) calc(var(--u) * 14); font-size: calc(var(--t) * 15.5); line-height: 1.4; }
  .acx-phone .acx-turn { margin-top: calc(var(--u) * 22); }
  .acx-phone .acx-label { gap: calc(var(--u) * 6); }
  .acx-phone .acx-label .ic { width: calc(var(--u) * 15); height: calc(var(--u) * 15); }
  .acx-phone .acx-steps { gap: calc(var(--u) * 10); margin-top: calc(var(--u) * 10); }
  .acx-phone .acx-step { gap: calc(var(--u) * 10); font-size: calc(var(--t) * 14.5); }
  .acx-phone .acx-node { width: calc(var(--u) * 20); height: calc(var(--u) * 20); }
  .acx-phone .acx-node .ic { width: calc(var(--u) * 12); height: calc(var(--u) * 12); }
  .acx-phone .acx-step.run .acx-node i { width: calc(var(--u) * 14); height: calc(var(--u) * 14); }
  .acx-phone .acx-reply p { margin-top: calc(var(--u) * 14); font-size: calc(var(--t) * 15.5); }
  .acx-phone .acx-card { margin-top: calc(var(--u) * 12); border-radius: calc(var(--u) * 18); }
  .acx-phone .acx-card-head { flex-wrap: wrap; gap: calc(var(--u) * 8) calc(var(--u) * 12); padding: calc(var(--u) * 14); }
  .acx-phone .acx-card-t { flex-basis: calc(100% - var(--u) * 60); }
  .acx-phone .acx-tile { width: calc(var(--u) * 36); height: calc(var(--u) * 36); }
  .acx-phone .acx-tile .ic { width: calc(var(--u) * 18); height: calc(var(--u) * 18); }
  .acx-phone .acx-card-t b { font-size: calc(var(--t) * 14.5); }
  .acx-phone .acx-card-t small, .acx-phone .acx-pill { font-size: calc(var(--t) * 12); }
  .acx-phone .acx-pill { margin-left: calc(var(--u) * 48); padding: calc(var(--u) * 3) calc(var(--u) * 9); }
  .acx-phone .acx-card-body { padding: calc(var(--u) * 12) calc(var(--u) * 14) calc(var(--u) * 14); }
  .acx-phone .acx-kv { gap: calc(var(--u) * 8) calc(var(--u) * 12); font-size: calc(var(--t) * 13); }
  .acx-phone .acx-inset { margin-top: calc(var(--u) * 12); padding: calc(var(--u) * 10) calc(var(--u) * 12); font-size: calc(var(--t) * 13); }
  .acx-phone .acx-card-foot { gap: calc(var(--u) * 16); padding: calc(var(--u) * 12) calc(var(--u) * 14); }
  .acx-phone .acx-send { gap: calc(var(--u) * 6); height: calc(var(--u) * 40); padding: 0 calc(var(--u) * 14); }
  .acx-phone .acx-send .ic { width: calc(var(--u) * 16); height: calc(var(--u) * 16); }
  .acx-phone .acx-compose { padding: calc(var(--u) * 8) calc(var(--u) * 12) calc(var(--u) * 12); }
  .acx-phone .acx-box { border-radius: calc(var(--u) * 14); padding: calc(var(--u) * 12) calc(var(--u) * 10) calc(var(--u) * 10) calc(var(--u) * 14); }
  .acx-phone .acx-ph { font-size: calc(var(--t) * 16); }
  .acx-phone .acx-tools { gap: calc(var(--u) * 6); margin-top: calc(var(--u) * 10); }
  .acx-phone .acx-plus, .acx-phone .acx-at, .acx-phone .acx-mic { width: calc(var(--u) * 36); height: calc(var(--u) * 36); }
  .acx-phone .acx-div, .acx-phone .acx-chip { display: none; }
  .acx-phone .acx-go { width: calc(var(--u) * 36); height: calc(var(--u) * 36); }
}
@media (prefers-reduced-motion: reduce) { .acx * { animation: none !important; transition: none !important; } }
</style>
